import React, { useState } from 'react';
import { ApprenticeSelector } from '../components/apprentices/ApprenticeSelector';
import { buildLevels } from '../lib/helpers';

export function TrainingPlanPage({ profile, myApprentices, allProfiles, selApprId, setSelApprId, units, trainingPlans, signoffs, workplaceSignoffs, knowledgeUnits, levelDates, onUpsertPlan }) {
  const activeId = profile.role === 'apprentice' ? profile.id : selApprId;
  const canEdit = profile.role === 'trainer' || profile.role === 'employer';

  const plan = trainingPlans.filter(p => p.apprentice_id === activeId);
  const levels = buildLevels(units, plan);

  // Get apprentice start date for year calculations
  const activeProfile = (allProfiles || []).find(p => p.id === activeId);
  const startDate = activeProfile?.start_date;

  function getYearRange(year) {
    if (!startDate) return null;
    const start = new Date(startDate);
    const yearStart = new Date(start);
    yearStart.setFullYear(yearStart.getFullYear() + (year - 1));
    const yearEnd = new Date(start);
    yearEnd.setFullYear(yearEnd.getFullYear() + year);
    yearEnd.setDate(yearEnd.getDate() - 1);
    return {
      start: yearStart.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
      end: yearEnd.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }),
    };
  }

  // Electives not yet assigned to any year
  const assignedCodes = plan.map(p => p.unit_code);
  const unassignedElectives = (units || []).filter(u => u.type === 'elective' && !assignedCodes.includes(u.code));

  const [saving, setSaving] = useState(null);
  const [showUnassigned, setShowUnassigned] = useState(false);

  async function handleYearChange(unitCode, newYear) {
    if (!activeId || !canEdit) return;
    setSaving(unitCode);
    try {
      await onUpsertPlan(activeId, unitCode, Number(newYear));
    } catch (e) {
      console.error('Failed to update plan:', e);
    }
    setSaving(null);
  }

  function getUnitStatus(unit) {
    const ws = workplaceSignoffs.find(s => s.apprentice_id === activeId && s.unit_code === unit.code);
    const ku = knowledgeUnits.find(k => k.apprentice_id === activeId && k.unit_code === unit.code);
    if (ws && ku) return 'complete';
    if (ws || ku) return 'partial';
    return 'not-started';
  }

  const totalPlanned = levels.reduce((s, l) => s + l.units.length, 0);
  const totalComplete = levels.reduce((s, l) => s + l.units.filter(u => getUnitStatus(u) === 'complete').length, 0);

  return (
    <div>
      <div className="page-hdr">
        <div>
          <div className="page-title">Training Plan</div>
          <div className="page-sub">
            {totalPlanned} units planned · {totalComplete} complete
            {!canEdit && ' · View only'}
          </div>
        </div>
      </div>

      {profile.role !== 'apprentice' && (
        <ApprenticeSelector apprentices={myApprentices} active={selApprId} onChange={setSelApprId} />
      )}

      {!activeId ? (
        <div className="empty">
          <div className="empty-icon">📐</div>
          <p>Select an apprentice to view their training plan</p>
        </div>
      ) : plan.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">📄</div>
          <p>No training plan loaded yet</p>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
            Go to Bulk Upload → Smart PDF Import to load a training plan from your RTO.
          </p>
        </div>
      ) : (
        <>
          {/* Year levels */}
          {levels.map(level => {
            const done = level.units.filter(u => getUnitStatus(u) === 'complete').length;
            const pct = level.units.length > 0 ? Math.round((done / level.units.length) * 100) : 0;
            const yearRange = getYearRange(level.year);

            return (
              <div key={level.year} className="card" style={{ marginBottom: 16 }}>
                <div className="card-hdr">
                  <div>
                    <span className="card-title">{level.label}</span>
                    {yearRange && (
                      <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                        {yearRange.start} — {yearRange.end}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 11, color: 'var(--muted)' }}>{done}/{level.units.length} complete</span>
                    <span style={{ fontFamily: 'var(--font-d)', fontWeight: 900, fontSize: 16, color: pct === 100 ? 'var(--green)' : 'var(--head)' }}>{pct}%</span>
                  </div>
                </div>

                {level.units.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                    No units assigned to this year
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Unit Code</th>
                          <th>Unit Name</th>
                          <th>Type</th>
                          <th>Status</th>
                          {canEdit && <th>Year</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {level.units.map(u => {
                          const status = getUnitStatus(u);
                          const isSaving = saving === u.code;

                          return (
                            <tr key={u.code}>
                              <td style={{ fontFamily: 'var(--font-d)', fontWeight: 700, color: 'var(--accent)', letterSpacing: 0.5, fontSize: 13 }}>
                                {u.code}
                              </td>
                              <td style={{ fontSize: 13 }}>{u.name}</td>
                              <td>
                                <span style={{
                                  fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
                                  color: u.type === 'core' ? 'var(--accent2)' : 'var(--muted)',
                                }}>
                                  {u.type}
                                </span>
                              </td>
                              <td>
                                {status === 'complete' && <span className="badge approved">Complete</span>}
                                {status === 'partial' && <span className="badge pending">In Progress</span>}
                                {status === 'not-started' && <span className="badge draft">Not Started</span>}
                              </td>
                              {canEdit && (
                                <td>
                                  <select
                                    className="fsel"
                                    style={{ width: 90, padding: '5px 9px', fontSize: 12, opacity: isSaving ? 0.5 : 1 }}
                                    value={level.year}
                                    disabled={isSaving}
                                    onChange={e => handleYearChange(u.code, e.target.value)}
                                  >
                                    <option value="1">Year 1</option>
                                    <option value="2">Year 2</option>
                                    <option value="3">Year 3</option>
                                  </select>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Unassigned electives */}
          {canEdit && unassignedElectives.length > 0 && (
            <div className="card">
              <div className="card-hdr">
                <span className="card-title">Unassigned Electives ({unassignedElectives.length})</span>
                <button className="btn-sm" onClick={() => setShowUnassigned(!showUnassigned)}>
                  {showUnassigned ? 'Hide' : 'Show'}
                </button>
              </div>
              {showUnassigned && (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Unit Code</th>
                        <th>Unit Name</th>
                        <th>Assign to Year</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unassignedElectives.map(u => (
                        <tr key={u.code}>
                          <td style={{ fontFamily: 'var(--font-d)', fontWeight: 700, color: 'var(--accent)', letterSpacing: 0.5, fontSize: 13 }}>
                            {u.code}
                          </td>
                          <td style={{ fontSize: 13 }}>{u.name}</td>
                          <td>
                            <select
                              className="fsel"
                              style={{ width: 90, padding: '5px 9px', fontSize: 12, opacity: saving === u.code ? 0.5 : 1 }}
                              value=""
                              disabled={saving === u.code}
                              onChange={e => { if (e.target.value) handleYearChange(u.code, e.target.value); }}
                            >
                              <option value="">—</option>
                              <option value="1">Year 1</option>
                              <option value="2">Year 2</option>
                              <option value="3">Year 3</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
